import { IonCard, IonCardHeader, IonCardTitle, IonImg } from '@ionic/react';
import { useEffect, useMemo } from 'react';

import { LOW_BATTERY_THRESHOLD } from '@/utils/constants/lowBatteryThreshold';
import { MappedSegment } from '@/utils/getFirebaseSystemByConfigId';
import { mergeClassNames } from '@/utils/mergeClassNames';

import { useSensor } from '@/hooks/useSensor';

import { useSensorsContext } from '@/contexts/SensorsContext';

import BackgroundPattern from '@/assets/bg-pattern-sensorcard.svg';
import SensorImage from '@/assets/sensors/sensor.svg';

import styles from './SensorCard.module.css';

import { SensorCardCalibrating } from './SensorCardCalibrating';
import { SensorCardNotCalibrated } from './SensorCardNotCalibrated';
import { SensorCardSearching } from './SensorCardSearching';
import { SensorCardSkeleton } from './SensorCardSkeleton';

export enum SensorStatus {
  SKELETON = 'skeleton',
  SEARCHING = 'searching',
  CONNECTED_NOT_CALIBRATED = 'connectedNotCalibrated',
  LOW_BATTERY = 'lowBattery',
  CALIBRATING = 'calibrating',
}

export interface Sensor extends Pick<SensorCardProps, 'id' | 'title' | 'state'> {
  segment: MappedSegment;
}

interface SensorCardProps {
  title: string;
  id: string;
  selected: boolean;
  onSelect?: (sensor: Sensor) => void;
  state: SensorStatus;
  segment: MappedSegment;
  isHubFlow: boolean;
  isOnCalibrationScreen?: boolean;
}

export const SensorCard = ({
  id,
  selected,
  onSelect,
  title,
  segment,
  isHubFlow,
  isOnCalibrationScreen,
}: SensorCardProps) => {
  const { batteryLevel, calibrationData } = useSensor(id, isHubFlow);
  const { state: sensorsState } = useSensorsContext();

  const state = useMemo(() => {
    if ((batteryLevel === undefined || batteryLevel < 0) && sensorsState.sensorsInDatabase.length === 0) {
      return SensorStatus.SKELETON;
    }

    if ((batteryLevel === undefined || batteryLevel < 0) && sensorsState.sensorsInDatabase.length > 0) {
      return SensorStatus.SEARCHING;
    }

    // If the sensor is calibrating, or if calibration has completed or failed, then display as calibrating
    if (
      sensorsState.isCalibrating ||
      sensorsState.allSuccessfullyCalibrated ||
      sensorsState.failedCalibrationSensors.length > 0 ||
      isOnCalibrationScreen
    ) {
      return SensorStatus.CALIBRATING;
    }

    // Battery level is low, user needs to charge to continue
    if (batteryLevel <= LOW_BATTERY_THRESHOLD && batteryLevel > -1) {
      return SensorStatus.LOW_BATTERY;
    }

    if (batteryLevel > -1) {
      return SensorStatus.CONNECTED_NOT_CALIBRATED;
    }

    return SensorStatus.SEARCHING;
  }, [
    batteryLevel,
    sensorsState.isCalibrating,
    sensorsState.connectedSensors,
    sensorsState.failedCalibrationSensors,
    sensorsState.allSuccessfullyCalibrated,
    isOnCalibrationScreen,
  ]);

  if (state === SensorStatus.SKELETON) {
    return <SensorCardSkeleton />;
  }

  return (
    <IonCard
      button
      className={mergeClassNames(
        styles.card,
        selected && styles.selected,
        state === SensorStatus.LOW_BATTERY && styles.lowBattery,
      )}
      onClick={() => onSelect?.({ id, title, state, segment })}>
      <div className={styles.content}>
        <IonCardHeader className={styles.header}>
          <IonCardTitle className={styles.title}>{title}</IonCardTitle>
        </IonCardHeader>

        {
          {
            [SensorStatus.LOW_BATTERY]: <SensorCardNotCalibrated batteryLevel={batteryLevel} />,
            [SensorStatus.SEARCHING]: <SensorCardSearching />,
            [SensorStatus.CONNECTED_NOT_CALIBRATED]: <SensorCardNotCalibrated batteryLevel={batteryLevel} />,
            [SensorStatus.CALIBRATING]: <SensorCardCalibrating calibrationStatus={calibrationData} />,
          }[state]
        }
      </div>

      {state === SensorStatus.CONNECTED_NOT_CALIBRATED && (
        <div className={styles.sensor}>
          <IonImg className={styles.sensorImage} src={SensorImage} />
          <IonImg className={styles.backgroundPattern} src={BackgroundPattern} />
        </div>
      )}
    </IonCard>
  );
};
